/*
  window.MMSWishlist — the storefront wishlist client (replaces Wishlist Hero).
  Loaded on every page by snippets/mms-wishlist-head.liquid, only while the theme setting
  mms_wishlist_enabled is on. It talks to the dashboard through the App Proxy
  (/apps/mms/wishlist/*, same origin). Shopify signs each proxied request and adds the
  signed-in customer, so the theme never sends a customer ID. API reference:
  mms-shopify-dashboard/docs/wishlist/api.md.

  MMSWishlist.state({ refresh })  -> Promise<state>
      The current state. Resolves from memory once the page's first state is known;
      { refresh: true } reads the server now (coalesced with any read in flight).
      state = { signedIn, items[], hidden_count, share_token, alerts_enabled }
      items in the display shape: { product_id, variant_id, title, variant_title, handle,
      image_url, price_cents, compare_at_cents, sellable, added_at }. An item added in
      this page that the server hasn't confirmed yet is { variant_id } only.
  MMSWishlist.add(variantId)      -> Promise<item>
  MMSWishlist.remove(variantId)   -> Promise
      Optimistic: the change shows at once (mms:wishlist-changed) and is rolled back if
      the server refuses it; a refusal also shows a short non-blocking message. Writes
      to one variant run in order, so rapid toggling settles on the last click.
  MMSWishlist.has(variantId)      -> boolean (synchronous, from the current state)
  MMSWishlist.count()             -> number of visible items (synchronous)
  MMSWishlist.isReady()           -> true once a state (cached or read) is known
  MMSWishlist.shared(shareToken)  -> Promise<{ items[], hidden_count } | null>  (null: unknown token)
  MMSWishlist.cartEvent(variantId, cartToken?)
                                     Analytics: a wishlist item was added to the cart.
                                     cartToken is /cart.js's token, sent as-is (the server
                                     strips "?key=…"); a 400 resends without it. Fire and
                                     forget.
  MMSWishlist.notify(message)        Shows the small non-blocking message.

  Event (on document): mms:wishlist-changed, detail { state, reason, variantId? }
      reason 'cache' | 'refresh' | 'add' | 'remove' | 'rollback'

  Elements
    <mms-wishlist-heart data-variant-id="…" [data-track="selected"]> wrapping a
      <button>: toggles that variant (snippets/mms-wishlist-heart.liquid). Keeps
      aria-pressed, its aria-label and any [data-mms-wishlist-heart-text] in step.
      data-track="selected" follows the product form's variant in the enclosing
      .product-section: variant:change on document carries the picked variant (used when
      it's one of this product's, per [data-variant-json]), and Impulse fires a bubbling
      change on the master [data-product-select] after it. The master select has no value
      for sold-out variants, so it only helps for available ones.
    [data-mms-wishlist-count] badges (snippets/mms-wishlist-count.liquid): the count,
      hidden at 0.

  Identity and storage (all storage wrapped, so a blocked storage only loses caching)
    - Guest token: localStorage mms_wishlist_guest_token, saved whenever a response
      carries guestToken. Sent with every state read (a signed-in read with it merges
      the guest list into the customer's; on merged: true it is dropped) and, for
      guests, with every write.
    - State cache: sessionStorage mms_wishlist_state for 5 minutes, so hearts render at
      once; ignored when the visitor signed in or out since. The server is still read
      once per page load (debounced) and again shortly after writes.
    - Wishlist Hero carry-over: if localStorage 'wishlisthero_.wl-h' (a JSON-encoded
      string) exists and mms_wishlist_wlh_done isn't '1', the first state read sends it
      as wlhHash, then mms_wishlist_wlh_done = '1' whatever the result, so it is sent
      once. Wishlist Hero's own keys are never changed.

  ?mms_open_cart=1 on any page: after load, refreshes the cart and opens the cart drawer
  (cart:open), then drops the parameter from the URL. Does nothing with cart type "page".
*/
(function () {
  'use strict';

  if (window.MMSWishlist) {
    return;
  }

  var BASE = '/apps/mms/wishlist/';
  var EVENT = 'mms:wishlist-changed';
  var GUEST_KEY = 'mms_wishlist_guest_token';
  var CACHE_KEY = 'mms_wishlist_state';
  var CACHE_TTL = 5 * 60 * 1000;
  var WLH_KEY = 'wishlisthero_.wl-h';
  var WLH_DONE_KEY = 'mms_wishlist_wlh_done';
  var LOAD_REFRESH_DELAY = 50;
  var WRITE_REFRESH_DELAY = 1500;
  var TIMEOUT = 15000;

  var config = readConfig();

  // ─── Storage ───────────────────────────────────────────────────────────────

  function storage(kind) {
    try { return window[kind]; } catch (e) { return null; }
  }
  function getItem(kind, key) {
    try { var s = storage(kind); return s ? s.getItem(key) : null; } catch (e) { return null; }
  }
  function setItem(kind, key, value) {
    try { var s = storage(kind); if (s) s.setItem(key, value); } catch (e) { /* ignore */ }
  }
  function removeItem(kind, key) {
    try { var s = storage(kind); if (s) s.removeItem(key); } catch (e) { /* ignore */ }
  }

  function readConfig() {
    try {
      var el = document.getElementById('mms-wishlist-config');
      return el ? JSON.parse(el.textContent) || {} : {};
    } catch (e) {
      return {};
    }
  }

  function guestToken() {
    return getItem('localStorage', GUEST_KEY) || null;
  }
  function saveGuestToken(token) {
    if (typeof token === 'string' && token) setItem('localStorage', GUEST_KEY, token);
  }

  function readCache() {
    try {
      var cached = JSON.parse(getItem('sessionStorage', CACHE_KEY) || 'null');
      if (!cached || !cached.state || typeof cached.at !== 'number') return null;
      if (Date.now() - cached.at > CACHE_TTL || Date.now() < cached.at) return null;
      if (!!cached.state.signedIn !== !!config.signedIn) return null;
      return normalize(cached.state);
    } catch (e) {
      return null;
    }
  }
  function writeCache(state) {
    try {
      setItem('sessionStorage', CACHE_KEY, JSON.stringify({ at: Date.now(), state: state }));
    } catch (e) { /* ignore */ }
  }

  // The Wishlist Hero hash to send once, or null.
  function wlhHash() {
    if (getItem('localStorage', WLH_DONE_KEY) === '1') return null;
    var raw = getItem('localStorage', WLH_KEY);
    if (!raw) return null;
    var hash = raw;
    try { hash = JSON.parse(raw); } catch (e) { /* not JSON: use as is */ }
    return typeof hash === 'string' && hash.trim() ? hash.trim() : null;
  }

  // ─── Requests ──────────────────────────────────────────────────────────────

  function WishlistError(status, type) {
    var err = new Error('Wishlist request failed');
    err.name = 'WishlistError';
    err.status = status || 0;
    err.type = type; // 'http' | 'timeout' | 'network'
    return err;
  }

  function request(method, path, body, options) {
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = controller ? setTimeout(function () { controller.abort(); }, TIMEOUT) : null;
    var init = {
      method: method,
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      signal: controller ? controller.signal : undefined
    };
    if (body) {
      init.headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(body);
    }
    if (options && options.keepalive) init.keepalive = true;
    return fetch(BASE + path, init).then(function (response) {
      if (timer) clearTimeout(timer);
      if (!response.ok) throw WishlistError(response.status, 'http');
      return response.json().catch(function () { throw WishlistError(response.status, 'http'); });
    }, function (error) {
      if (timer) clearTimeout(timer);
      throw WishlistError(0, error && error.name === 'AbortError' ? 'timeout' : 'network');
    });
  }

  function identity(body, forWrite) {
    var token = guestToken();
    // Writes are the guest's only identity; a signed-in write is the customer's.
    if (token && !(forWrite && isSignedIn())) body.guestToken = token;
    return body;
  }

  // ─── State ─────────────────────────────────────────────────────────────────

  var server = null;   // the last state the server confirmed (or the cache)
  var pending = {};    // variantId -> { op: 'add'|'remove', seq }
  var queues = {};     // variantId -> promise chain of writes
  var seq = 0;
  var writesSettled = 0;
  var refreshInFlight = null;
  var refreshTimer = null;
  var readyWaiters = [];

  function normalize(state) {
    state = state || {};
    return {
      signedIn: !!state.signedIn,
      items: Array.isArray(state.items) ? state.items.filter(function (i) { return i && i.variant_id != null; }) : [],
      hidden_count: Number(state.hidden_count) || 0,
      share_token: state.share_token || null,
      alerts_enabled: !!state.alerts_enabled
    };
  }

  function isSignedIn() {
    return server ? server.signedIn : !!config.signedIn;
  }

  // The state as shown: the server's, with the writes still in flight applied.
  function view() {
    var base = server || normalize({ signedIn: config.signedIn });
    var items = base.items.filter(function (item) {
      var p = pending[String(item.variant_id)];
      return !(p && p.op === 'remove');
    });
    Object.keys(pending).forEach(function (id) {
      if (pending[id].op !== 'add') return;
      var present = items.some(function (item) { return String(item.variant_id) === id; });
      if (!present) items.unshift({ variant_id: id });
    });
    return {
      signedIn: base.signedIn,
      items: items,
      hidden_count: base.hidden_count,
      share_token: base.share_token,
      alerts_enabled: base.alerts_enabled
    };
  }

  function emit(reason, variantId) {
    var state = view();
    updateBadges(state);
    document.dispatchEvent(new CustomEvent(EVENT, {
      detail: { state: state, reason: reason, variantId: variantId == null ? undefined : String(variantId) }
    }));
  }

  function setServer(state) {
    server = normalize(state);
    writeCache(server);
    var waiters = readyWaiters;
    readyWaiters = [];
    waiters.forEach(function (resolve) { resolve(view()); });
  }

  function refresh() {
    if (refreshTimer) {
      clearTimeout(refreshTimer);
      refreshTimer = null;
    }
    if (refreshInFlight) return refreshInFlight;

    var startedAt = writesSettled;
    var body = identity({}, false);
    var hash = wlhHash();
    if (hash) body.wlhHash = hash;

    refreshInFlight = request('POST', 'state', body).then(function (data) {
      if (hash) setItem('localStorage', WLH_DONE_KEY, '1');
      if (data.guestToken) saveGuestToken(data.guestToken);
      if (data.merged) removeItem('localStorage', GUEST_KEY);
      refreshInFlight = null;
      if (writesSettled !== startedAt) {
        // A write landed while this read was out: its answer may be stale.
        scheduleRefresh(0);
        return view();
      }
      setServer(data);
      emit('refresh');
      return view();
    }, function (error) {
      if (hash) setItem('localStorage', WLH_DONE_KEY, '1');
      refreshInFlight = null;
      throw error;
    });
    return refreshInFlight;
  }

  function scheduleRefresh(delay) {
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = setTimeout(function () {
      refreshTimer = null;
      refresh().catch(function () { /* keep showing what we have */ });
    }, delay);
  }

  function state(options) {
    if (options && options.refresh) return refresh();
    if (server) return Promise.resolve(view());
    return new Promise(function (resolve, reject) {
      readyWaiters.push(resolve);
      // No state yet: read now rather than waiting for the scheduled read.
      refresh().catch(function (error) {
        var i = readyWaiters.indexOf(resolve);
        if (i !== -1) readyWaiters.splice(i, 1);
        reject(error);
      });
    });
  }

  function variantKey(variantId) {
    var id = String(variantId == null ? '' : variantId).trim();
    return /^\d+$/.test(id) ? id : null;
  }

  function write(op, variantId) {
    var id = variantKey(variantId);
    if (!id) return Promise.reject(WishlistError(0, 'http'));
    var mySeq = ++seq;
    pending[id] = { op: op, seq: mySeq };
    emit(op, id);

    var run = function () {
      return request('POST', op, identity({ variantId: id }, true));
    };
    var chain = (queues[id] || Promise.resolve()).then(run, run);
    queues[id] = chain.catch(function () {});

    return chain.then(function (data) {
      writesSettled++;
      if (data && data.guestToken) saveGuestToken(data.guestToken);
      var base = server || normalize({ signedIn: config.signedIn });
      var items = base.items.filter(function (item) { return String(item.variant_id) !== id; });
      if (op === 'add' && data && data.item) items.unshift(data.item);
      server = Object.assign({}, base, { items: items });
      if (op === 'add' && data && data.share_token) server.share_token = data.share_token;
      writeCache(server);
      if (pending[id] && pending[id].seq === mySeq) delete pending[id];
      emit(op, id);
      scheduleRefresh(WRITE_REFRESH_DELAY);
      return op === 'add' ? data.item : data;
    }, function (error) {
      writesSettled++;
      if (pending[id] && pending[id].seq === mySeq) delete pending[id];
      emit('rollback', id);
      notify(messageFor(op, error));
      scheduleRefresh(WRITE_REFRESH_DELAY);
      throw error;
    });
  }

  function messageFor(op, error) {
    var status = error && error.status;
    if (op === 'add' && status === 409) return 'Your wishlist is full. Remove an item to save this one.';
    if (op === 'add' && status === 404) return "This item can't be saved to your wishlist.";
    if (status === 429) return 'Too many changes at once. Please try again in a moment.';
    return op === 'add'
      ? "We couldn't save that to your wishlist. Please try again."
      : "We couldn't remove that from your wishlist. Please try again.";
  }

  function has(variantId) {
    var id = variantKey(variantId);
    if (!id) return false;
    return view().items.some(function (item) { return String(item.variant_id) === id; });
  }

  function count() {
    return view().items.length;
  }

  function shared(token) {
    if (typeof token !== 'string' || !/^[A-Za-z0-9_-]{1,128}$/.test(token)) return Promise.resolve(null);
    return request('GET', 'shared/' + encodeURIComponent(token)).then(function (data) {
      return {
        items: Array.isArray(data.items) ? data.items : [],
        hidden_count: Number(data.hidden_count) || 0
      };
    }, function (error) {
      if (error.status === 404) return null;
      throw error;
    });
  }

  function cartEvent(variantId, cartToken) {
    var id = variantKey(variantId);
    if (!id) return;
    var body = identity({ variantId: id }, true);
    if (typeof cartToken === 'string' && cartToken) body.cartToken = cartToken;
    request('POST', 'cart-event', body, { keepalive: true }).catch(function (error) {
      // A token the server won't take (400) must not cost the event: send it without.
      if (body.cartToken && error && error.status === 400) {
        delete body.cartToken;
        request('POST', 'cart-event', body, { keepalive: true }).catch(function () {});
      }
    });
  }

  // ─── The small message ────────────────────────────────────────────────────

  var toast = null;
  var toastTimer = null;

  function notify(message) {
    if (!message || !document.body) return;
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'mms-wishlist-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      toast.hidden = true;
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.hidden = false;
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { toast.hidden = true; }, 4500);
  }

  // ─── Count badges ──────────────────────────────────────────────────────────

  function updateBadges(state) {
    var n = state.items.length;
    var label = n === 1 ? '1 saved item' : n + ' saved items';
    Array.prototype.forEach.call(document.querySelectorAll('[data-mms-wishlist-count]'), function (badge) {
      var number = badge.querySelector('[data-mms-wishlist-count-number]');
      var text = badge.querySelector('[data-mms-wishlist-count-label]');
      if (number) number.textContent = String(n);
      if (text) text.textContent = label;
      badge.hidden = n === 0;
    });
  }

  window.MMSWishlist = {
    state: state,
    add: function (variantId) { return write('add', variantId); },
    remove: function (variantId) { return write('remove', variantId); },
    has: has,
    count: count,
    isReady: function () { return !!server; },
    shared: shared,
    cartEvent: cartEvent,
    notify: notify,
    EVENT: EVENT
  };

  // ─── <mms-wishlist-heart> ─────────────────────────────────────────────────

  if (!customElements.get('mms-wishlist-heart')) {
    customElements.define(
      'mms-wishlist-heart',
      class MMSWishlistHeart extends HTMLElement {
        connectedCallback() {
          this.button = this.querySelector('button');
          if (!this.button) return;
          this.text = this.querySelector('[data-mms-wishlist-heart-text]');
          this.variantId = this.dataset.variantId || '';

          if (!this._onClick) {
            this._onClick = this.toggle.bind(this);
            this._onChanged = this.render.bind(this);
            this._onFormChange = this.onFormChange.bind(this);
            this._onVariantChange = this.onVariantChange.bind(this);
          }
          this.button.addEventListener('click', this._onClick);
          document.addEventListener(EVENT, this._onChanged);

          if (this.dataset.track === 'selected') {
            this.root = this.closest('.product-section') || this.closest('form') || null;
            if (this.root) {
              this.root.addEventListener('change', this._onFormChange);
              document.addEventListener('variant:change', this._onVariantChange);
              // No initial sync: Liquid rendered the selected variant, and the master select
              // shows the first available one when the selected variant is sold out.
            }
          }
          this.render();
        }

        disconnectedCallback() {
          if (this.button) this.button.removeEventListener('click', this._onClick);
          document.removeEventListener(EVENT, this._onChanged);
          if (this.root) this.root.removeEventListener('change', this._onFormChange);
          document.removeEventListener('variant:change', this._onVariantChange);
          this.root = null;
        }

        selectedVariantId() {
          if (!this.root) return '';
          var select = this.root.querySelector('[data-product-select]') || this.root.querySelector('[name="id"]');
          return select && select.value ? select.value : '';
        }

        syncVariant() {
          var id = this.selectedVariantId();
          if (id && id !== this.variantId) {
            this.variantId = id;
            this.dataset.variantId = id;
            this.render();
          }
        }

        onFormChange(evt) {
          var target = evt.target;
          if (target && target.matches && target.matches('[data-product-select], [name="id"]')) this.syncVariant();
        }

        // This product's variant IDs, from the product form's [data-variant-json].
        ownsVariant(id) {
          if (!this._variantIds) {
            this._variantIds = {};
            try {
              var json = this.root && this.root.querySelector('[data-variant-json]');
              (json ? JSON.parse(json.value || json.textContent) : []).forEach(function (v) {
                this._variantIds[String(v.id)] = true;
              }, this);
            } catch (e) { /* fall back to the master select */ }
          }
          return !!this._variantIds[String(id)];
        }

        onVariantChange(evt) {
          // The event's variant is the one picked, sold out or not (the master select has
          // no value for sold-out variants). It isn't scoped to a form, so only take a
          // variant of this product.
          var variant = evt.detail && evt.detail.variant;
          if (variant && this.ownsVariant(variant.id)) {
            if (String(variant.id) !== this.variantId) {
              this.variantId = String(variant.id);
              this.dataset.variantId = this.variantId;
              this.render();
            }
            return;
          }
          // Impulse fires variant:change before it updates the master select.
          setTimeout(this.syncVariant.bind(this), 0);
        }

        toggle(evt) {
          evt.preventDefault();
          evt.stopPropagation();
          if (!variantKey(this.variantId)) return;
          var action = has(this.variantId) ? window.MMSWishlist.remove : window.MMSWishlist.add;
          action(this.variantId).catch(function () { /* rolled back and reported */ });
        }

        render() {
          if (!this.button) return;
          var saved = has(this.variantId);
          var label = saved ? 'Remove from wishlist' : 'Save to wishlist';
          this.button.setAttribute('aria-pressed', saved ? 'true' : 'false');
          this.button.setAttribute('aria-label', label);
          if (this.text) this.text.textContent = label;
        }
      }
    );
  }

  // ─── Page load ─────────────────────────────────────────────────────────────

  var cached = readCache();
  if (cached) {
    server = cached;
    emit('cache');
  }
  scheduleRefresh(LOAD_REFRESH_DELAY);

  // ─── ?mms_open_cart=1 ──────────────────────────────────────────────────────
  // Pages that add to the cart and then send the shopper here (e.g. the customer account
  // Wishlist page's add to cart) ask for the drawer with ?mms_open_cart=1. After load:
  // refresh MMSCart's view of the cart, then cart:open (Impulse's drawer rebuilds the cart
  // and opens, as after its own add to cart). The parameter is removed first, so a reload
  // doesn't reopen. With the cart type set to "page", nothing happens.
  function openCartFromParam() {
    var params;
    try { params = new URLSearchParams(window.location.search); } catch (e) { return; }
    if (params.get('mms_open_cart') !== '1') return;
    var cartType = window.theme && theme.settings && theme.settings.cartType;
    if (cartType !== 'drawer') return;

    params.delete('mms_open_cart');
    var query = params.toString();
    try {
      history.replaceState(history.state, '', window.location.pathname + (query ? '?' + query : '') + window.location.hash);
    } catch (e) { /* the drawer still opens */ }

    var refreshed = window.MMSCart && MMSCart.refreshCart
      ? MMSCart.refreshCart().catch(function () {})
      : Promise.resolve();
    refreshed.then(function () {
      document.dispatchEvent(new CustomEvent('cart:open'));
    });
  }

  if (document.readyState === 'complete') {
    openCartFromParam();
  } else {
    window.addEventListener('load', openCartFromParam);
  }
})();
